'use client';
import React, { useRef, useState, useCallback } from 'react';
import type { PlacedComponent, Wire, WireColor, ComponentType } from '../../simulator-page/components/circuitTypes';
import { COMPONENT_DEFINITIONS, WIRE_COLOR_MAP } from '../../simulator-page/components/circuitTypes';
import CanvasComponent from '../../simulator-page/components/CanvasComponent';

interface WireInProgress {
  fromComponentId: string;
  fromTerminalId: string;
  fromX: number;
  fromY: number;
  color: WireColor;
  mouseX: number;
  mouseY: number;
}

interface Props {
  components: PlacedComponent[];
  wires: Wire[];
  selectedColor: WireColor;
  simulationMode: boolean;
  litLamps: Set<string>;
  selectedComponentId: string | null;
  onSelectComponent: (id: string | null) => void;
  onDropComponent: (type: ComponentType, x: number, y: number) => void;
  onMoveComponent: (id: string, x: number, y: number) => void;
  onDeleteComponent: (id: string) => void;
  onAddWire: (wire: Wire) => void;
  onDeleteWire: (wireId: string) => void;
  onToggleSwitch: (id: string) => void;
  wireCounterRef: React.MutableRefObject<number>;
}

function snapToGrid(v: number, grid = 20) {
  return Math.round(v / grid) * grid;
}

export default function PracticeCanvas({
  components, wires, selectedColor, simulationMode, litLamps,
  selectedComponentId, onSelectComponent,
  onDropComponent, onMoveComponent, onDeleteComponent,
  onAddWire, onDeleteWire, onToggleSwitch, wireCounterRef,
}: Props) {
  const svgRef = useRef<SVGSVGElement>(null);
  const [wireInProgress, setWireInProgress] = useState<WireInProgress | null>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isDragOver, setIsDragOver] = useState(false);

  const getSVGPoint = useCallback((e: React.MouseEvent | MouseEvent) => {
    const svg = svgRef.current;
    if (!svg) return { x: 0, y: 0 };
    const rect = svg.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }, []);

  const handleTerminalClick = useCallback(
    (compId: string, terminalIdx: number, absX: number, absY: number) => {
      if (!wireInProgress) {
        setWireInProgress({
          fromComponentId: compId,
          fromTerminalId: `${compId}-t${terminalIdx}`,
          fromX: absX,
          fromY: absY,
          color: selectedColor,
          mouseX: absX,
          mouseY: absY,
        });
      } else {
        const terminalId = `${compId}-t${terminalIdx}`;
        if (wireInProgress.fromComponentId === compId && wireInProgress.fromTerminalId === terminalId) {
          setWireInProgress(null);
          return;
        }
        wireCounterRef.current += 1;
        onAddWire({
          id: `pwire-${wireCounterRef.current}`,
          fromComponentId: wireInProgress.fromComponentId,
          fromTerminalId: wireInProgress.fromTerminalId,
          toComponentId: compId,
          toTerminalId: terminalId,
          color: wireInProgress.color,
        });
        setWireInProgress(null);
      }
    },
    [wireInProgress, selectedColor, onAddWire, wireCounterRef]
  );

  const handleSVGMouseMove = useCallback(
    (e: React.MouseEvent<SVGSVGElement>) => {
      if (wireInProgress) {
        const pt = getSVGPoint(e);
        setWireInProgress((prev) => prev ? { ...prev, mouseX: pt.x, mouseY: pt.y } : null);
      }
      if (draggingId) {
        const pt = getSVGPoint(e);
        onMoveComponent(draggingId, snapToGrid(pt.x - dragOffset.x), snapToGrid(pt.y - dragOffset.y));
      }
    },
    [wireInProgress, draggingId, dragOffset, getSVGPoint, onMoveComponent]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent<SVGSVGElement>) => {
      e.preventDefault();
      setIsDragOver(false);
      const type = e.dataTransfer.getData('componentType') as ComponentType;
      if (!type) return;
      const pt = getSVGPoint(e as unknown as React.MouseEvent);
      onDropComponent(type, snapToGrid(pt.x - 35), snapToGrid(pt.y - 35));
    },
    [getSVGPoint, onDropComponent]
  );

  return (
    <div className="flex-1 relative overflow-hidden">
      {components.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
          <div className="text-center">
            <div className="text-5xl mb-4">🔌</div>
            <p className="text-base font-semibold text-slate-400 mb-1">Canvas is empty</p>
            <p className="text-sm text-slate-300 mb-1">Drag components from the sidebar in the main simulator</p>
            <p className="text-xs text-slate-300">or use the component types listed in the exercise requirements</p>
          </div>
        </div>
      )}

      {/* Component quick-add bar */}
      <div className="absolute top-3 left-3 z-20 flex flex-wrap gap-1.5 max-w-xs">
        {[
          { type: 'supply' as ComponentType, emoji: '⚡', label: 'Supply' },
          { type: 'lamp' as ComponentType, emoji: '💡', label: 'Lamp' },
          { type: 'switch-one-way' as ComponentType, emoji: '🔲', label: '1-Way' },
          { type: 'switch-two-way' as ComponentType, emoji: '🔀', label: '2-Way' },
          { type: 'socket' as ComponentType, emoji: '🔌', label: 'Socket' },
          { type: 'mcb' as ComponentType, emoji: '🛡', label: 'MCB' },
          { type: 'rcbo' as ComponentType, emoji: '🔒', label: 'RCBO' },
          { type: 'neutral-bar' as ComponentType, emoji: '🔵', label: 'N-Bar' },
          { type: 'earth-bar' as ComponentType, emoji: '🟢', label: 'E-Bar' },
        ].map((item) => (
          <button
            key={`quick-${item.type}`}
            draggable
            onDragStart={(e) => e.dataTransfer.setData('componentType', item.type)}
            onClick={() => onDropComponent(item.type, snapToGrid(80 + Math.floor(Math.random() * 3) * 120), snapToGrid(80 + Math.floor(Math.random() * 3) * 100))}
            className="flex items-center gap-1 px-2 py-1 bg-white/90 backdrop-blur-sm border border-border rounded-lg text-xs font-medium text-foreground hover:bg-primary/10 hover:border-primary/30 transition-all duration-150 shadow-card cursor-grab active:cursor-grabbing"
            title={`Add ${item.label}`}
          >
            <span>{item.emoji}</span>
            <span className="hidden sm:inline">{item.label}</span>
          </button>
        ))}
      </div>

      <svg
        ref={svgRef}
        className={`w-full h-full select-none ${isDragOver ? 'bg-blue-50' : 'bg-slate-50'}`}
        style={{ cursor: wireInProgress ? 'crosshair' : draggingId ? 'grabbing' : 'default' }}
        onMouseMove={handleSVGMouseMove}
        onMouseUp={() => setDraggingId(null)}
        onClick={(e) => {
          if (e.target === svgRef.current || (e.target as SVGElement).tagName === 'rect' && (e.target as SVGElement).getAttribute('fill') === 'url(#practice-grid-major)') {
            onSelectComponent(null);
            if (wireInProgress) setWireInProgress(null);
          }
        }}
        onDrop={handleDrop}
        onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
        onDragLeave={() => setIsDragOver(false)}
      >
        <defs>
          <pattern id="practice-grid" width="20" height="20" patternUnits="userSpaceOnUse">
            <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#e2e8f0" strokeWidth="0.5"/>
          </pattern>
          <pattern id="practice-grid-major" width="100" height="100" patternUnits="userSpaceOnUse">
            <rect width="100" height="100" fill="url(#practice-grid)" />
            <path d="M 100 0 L 0 0 0 100" fill="none" stroke="#cbd5e1" strokeWidth="0.8"/>
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#practice-grid-major)" />

        {/* Wires */}
        {wires.map((wire) => {
          const fromComp = components.find((c) => c.instanceId === wire.fromComponentId);
          const toComp = components.find((c) => c.instanceId === wire.toComponentId);
          if (!fromComp || !toComp) return null;
          const fromDef = COMPONENT_DEFINITIONS[fromComp.type];
          const toDef = COMPONENT_DEFINITIONS[toComp.type];
          const fromIdx = parseInt(wire.fromTerminalId.split('-t')[1], 10);
          const toIdx = parseInt(wire.toTerminalId.split('-t')[1], 10);
          const fromT = fromDef.terminals[fromIdx];
          const toT = toDef.terminals[toIdx];
          if (!fromT || !toT) return null;
          const x1 = fromComp.x + fromT.relX;
          const y1 = fromComp.y + fromT.relY;
          const x2 = toComp.x + toT.relX;
          const y2 = toComp.y + toT.relY;
          const midX = (x1 + x2) / 2;
          const strokeColor = WIRE_COLOR_MAP[wire.color].hex;
          return (
            <g key={wire.id}>
              <path
                d={`M ${x1} ${y1} C ${midX} ${y1} ${midX} ${y2} ${x2} ${y2}`}
                stroke={strokeColor}
                strokeWidth="2.5"
                fill="none"
                strokeLinecap="round"
              />
              <path
                d={`M ${x1} ${y1} C ${midX} ${y1} ${midX} ${y2} ${x2} ${y2}`}
                stroke="transparent"
                strokeWidth="12"
                fill="none"
                className="cursor-pointer"
                onClick={(e) => { e.stopPropagation(); onDeleteWire(wire.id); }}
              />
            </g>
          );
        })}

        {/* Wire in progress */}
        {wireInProgress && (
          <path
            d={`M ${wireInProgress.fromX} ${wireInProgress.fromY} L ${wireInProgress.mouseX} ${wireInProgress.mouseY}`}
            stroke={WIRE_COLOR_MAP[wireInProgress.color].hex}
            strokeWidth="2.5"
            fill="none"
            strokeDasharray="6 4"
            strokeLinecap="round"
            className="pointer-events-none"
          />
        )}

        {/* Components */}
        {components.map((comp) => (
          <CanvasComponent
            key={comp.instanceId}
            component={comp}
            isSelected={selectedComponentId === comp.instanceId}
            isLit={litLamps.has(comp.instanceId)}
            simulationMode={simulationMode}
            wireInProgress={wireInProgress !== null}
            onMouseDown={(e) => {
              e.stopPropagation();
              if (wireInProgress) return;
              onSelectComponent(comp.instanceId);
              const svg = svgRef.current;
              if (!svg) return;
              const rect = svg.getBoundingClientRect();
              setDraggingId(comp.instanceId);
              setDragOffset({ x: e.clientX - rect.left - comp.x, y: e.clientY - rect.top - comp.y });
            }}
            onTerminalClick={handleTerminalClick}
            onDelete={() => onDeleteComponent(comp.instanceId)}
            onToggleSwitch={() => onToggleSwitch(comp.instanceId)}
          />
        ))}
      </svg>
    </div>
  );
}